import { createPopup, IPopup, IPopupOptions } from "../technical/popup-new";
import { expressEventListener, expressQuerySelector, expressQuerySelectorAll } from "../../common/html";
import { getToken } from "../../services/auth-service";
import { addParameter, getParameter } from "../../utils/querystring";
import {
	getPhotosAsync,
	getPhotoStoreAsync,
	copyPhotosToFolder,
	IPhotoStore,
	PartnerPhoto,
	StoreType,
} from "../../services/storageService";
import { connectToAsync } from "../technical/photostoreconnector";
import { Downloader } from "../technical/downloader";
import { showLoadingDialog } from "../popups/loading-dialog";
import { ExternalPhotoPicker } from "../technical/externalphotopicker";
import { compositeDisposable, IDisposable } from "../../utils/disposable";
import { createUpload, FlowType } from "../technical/upload";
import { createUploadDialog, IUploadDialog } from "../popups/upload-dialog";
import { createUploadIndicator } from "../technical/upload-indicator";
import { getCookie } from "../../utils/cookie";
import { createPageLog, IPageLog, PageLogStyle } from "../molecules/page-log";
import { getLabelAsync } from "../../services/content-service";
import { isSafari } from "../../common/browser";
import { createSessionPoller, ISessionPoller } from "../technical/session-poller";
import { initTooltip } from "../molecules/tooltip";

export interface IPhotoImport {
	selection: IPhoto[] | null;
	storeType: StoreType;
}

export interface IPhoto {
	readonly code: string;
	readonly name: string;
	readonly description: string;
	readonly originalFilename: string;
	readonly thumbUrl: string;
	readonly mediumUrl: string;
	readonly width: number;
	readonly height: number;
	readonly fileSize: number;
	readonly fileId: string;
	readonly dateTaken?: Date;
	readonly dateUploaded?: Date;
	readonly dateModified?: Date;
	readonly rotate90?: number;
}

export class photoStorePopupOptions {
	readonly onBeforeClose?: () => void = () => {
		return;
	};
	readonly onAfterUpload?: () => void = () => {
		return;
	};
	readonly errorMessage?: string = undefined;
}

export class PhotoSourcesPopup {
	private externalPicker: ExternalPhotoPicker | undefined;
	private pickerWindow: Window | null | undefined;
	private selectPhotoSourcePopup: IPopup | undefined;
	private uploadFromPhonePopup: IPopup | undefined;
	private connectedSourcePopup: IPopup | undefined;
	private maxSelectableItems: number;
	private onImportPhotosFinished: (photos: IPhoto[], failedFileNames: string[]) => Promise<void>;
	private refreshPhotoContainer?: () => void;
	private photoSourcesEventDisposables: IDisposable = compositeDisposable(undefined);
	private pageLog?: IPageLog;
	private imageInvalidLabel: string | undefined;
	private allFailedLabel: string | undefined;
	private someFailedLabel: string | undefined;
	private connectedStoreType?: StoreType;
	private broadcastChannel?: BroadcastChannel;
	private photoSourceTypesToShow: number[];
	private flowType: FlowType;
	private folderId?: string;
	private isBlockingUpload: boolean;
	private uploadIndicator?: HTMLElement;
	private _isUploadInProgress = false;

	private photostoresData: { [id: string]: IPhotoStore } = {};

	constructor(
		flowType: FlowType,
		onImportPhotosFinished: (photos: IPhoto[], failedPollingFileNames: string[]) => Promise<void>,
		maxSelectableItems = 0,
		photoSourceTypesToShow: number[] = [],
		folderId?: string,
		isBlockingUpload = true,
		uploadIndicator = null,
		refreshPhotoContainer?: () => void,
		pageLog?: IPageLog,
	) {
		this.flowType = flowType;
		this.onImportPhotosFinished = onImportPhotosFinished;
		this.maxSelectableItems = maxSelectableItems;
		this.photoSourceTypesToShow = photoSourceTypesToShow;
		this.folderId = folderId;
		this.isBlockingUpload = isBlockingUpload;
		this.uploadIndicator = uploadIndicator;
		this.refreshPhotoContainer = refreshPhotoContainer;
		this.pageLog = pageLog;
	}

	public isUploadInProgress = () => this._isUploadInProgress;

	public openPhotoStoresPopup = async (popupOptions?: photoStorePopupOptions) => {
		// create and open the popup
		this.selectPhotoSourcePopup = await createPopup({
			dialogId: "PhotoSourcesDialog",
			view: "PhotoSourcesDialog",
			forceRefresh: true,
			data: {
				MaxSelectableItems: this.maxSelectableItems,
				PhotoSourceTypesToShow: this.photoSourceTypesToShow,
				FolderId: this.folderId,
			},
			onBeforeClose: () => {
				this.closeConnectionAsync();
				this.photoSourcesEventDisposables.dispose();
				popupOptions?.onBeforeClose && popupOptions?.onBeforeClose();
			},
		});

		const pageLogEl = expressQuerySelector<HTMLElement>(
			this.selectPhotoSourcePopup.popup,
			".technical-top-message-log",
			false,
		);

		if (!this.pageLog && pageLogEl) this.pageLog = createPageLog(pageLogEl);

		if (this.pageLog && popupOptions?.errorMessage) {
			this.imageInvalidLabel = pageLogEl.parentElement.dataset.imageInvalidLabel;
			this.pageLog.updateMessageAndStyle(PageLogStyle.Error, popupOptions?.errorMessage, true);
		}
		this.allFailedLabel = pageLogEl.parentElement.dataset.allFailedLabel;
		this.someFailedLabel = pageLogEl.parentElement.dataset.someFailedLabel;

		this.initDeviceUpload();
		this.initPhoneUpload();
		this.initPhotoSources();

		await this.selectPhotoSourcePopup.openAsync();
	};

	private initDeviceUpload = (): void => {
		const fromDeviceEls = expressQuerySelectorAll<HTMLElement>(document, ".technical-upload-from-device");

		fromDeviceEls.forEach((fromDeviceEl) => {
			this.initUploadFromDevice(fromDeviceEl);
			expressEventListener(fromDeviceEl, "click", (e: Event) => {
				const eventTarget = e.target as HTMLElement;
				if (eventTarget.tagName == "INPUT") {
					return;
				} else {
					e.preventDefault();
				}

				this.startUploadFromDevice(fromDeviceEl);
			});
		});
	};

	private initPhoneUpload = (): void => {
		const fromPhoneEls = expressQuerySelectorAll<HTMLElement>(document, ".technical-upload-from-phone");
		fromPhoneEls.forEach((fromPhoneEl) => {
			expressEventListener(fromPhoneEl, "click", (e: Event) => {
				e.preventDefault();
				this.initUploadFromPhone(fromPhoneEl);
			});
		});
	};

	private startUploadFromDevice = async (parentEl: HTMLElement): Promise<void> => {
		const fileEl = expressQuerySelector<HTMLInputElement>(parentEl, 'input[type="file"]', true);
		fileEl.click();
	};

	private initUploadFromDevice = async (parentEl: HTMLElement): Promise<void> => {
		const fromDeviceEventDisposables: IDisposable = compositeDisposable(undefined);
		const fileEl = expressQuerySelector<HTMLInputElement>(parentEl, 'input[type="file"]', true);

		const processUploadFiles = async (ev: Event) => {
			const fileEl = ev.target as HTMLInputElement;
			const files = fileEl.files;
			if (!files || files.length == 0) return;

			this._isUploadInProgress = true;
			fromDeviceEventDisposables.dispose();

			const uploadDialog = this.isBlockingUpload
				? await createUploadDialog(files.length)
				: createUploadIndicator(files.length, this.uploadIndicator, this.refreshPhotoContainer);

			if (!this.isBlockingUpload) this.selectPhotoSourcePopup.close();

			const onUploadFinished = (
				folderId: string,
				importedFileIds: string[] | null,
				failedUploadAmount: number,
				failedFileNames: string[] | null,
			) => {
				this._isUploadInProgress = false;

				uploadDialog &&
					uploadDialog.updateProgress &&
					uploadDialog.updateProgress(importedFileIds.length + failedUploadAmount);

				if (!importedFileIds || !importedFileIds.length || failedUploadAmount != 0) {
					uploadDialog && uploadDialog.close();
					// Show the page log with the error message
					if (this.pageLog) {
						const isError = importedFileIds.length == 0;
						const message = isError
							? this.allFailedLabel.replace(/\{0\}/g, failedUploadAmount.toString())
							: this.someFailedLabel
									.replace(/\{0\}/g, failedUploadAmount.toString())
									.replace(/\{1\}/g, failedFileNames.join(","))
									.replace(/\{2\}/g, importedFileIds.length.toString());
						this.pageLog.updateMessageAndStyle(
							isError ? PageLogStyle.Error : PageLogStyle.WarningOrange,
							message,
							true,
						);
					}

					if (!importedFileIds.length) {
						this.onImportPhotosFinished([], failedFileNames);
						return;
					}
				}

				getPhotosAsync(window.context.pointOfSaleId, getCookie("accid"), folderId, importedFileIds).then(
					async (smartphotoPhotos) => {
						uploadDialog && uploadDialog.close();
						try {
							this.selectPhotoSourcePopup.close();
							await this.onImportPhotosFinished(smartphotoPhotos, failedFileNames);
						} catch (errorMessage) {
							await this.openPhotoStoresPopup({
								onBeforeClose: () => {
									return;
								},
								errorMessage,
							});
						}
					},
				);
			};

			const onFileUploaded = (amountUploadedFiles: number) => {
				uploadDialog && uploadDialog.updateProgress && uploadDialog.updateProgress(amountUploadedFiles);
			};

			const onBeforeUploadStart = () => {
				uploadDialog && uploadDialog.updateProgress && uploadDialog.updateProgress(0);
			};

			const folderId = parentEl.dataset.folderid || "";
			const uploadBatchSize = parseInt(parentEl.dataset.uploadbatchsize || "5", 10);

			// add the uploadDialog and finish methods with settings to the upload component
			await createUpload(files, folderId, "", getCookie("accid"), {
				onBeforeUploadStart,
				onUploadFinished,
				onFileUploaded,
				batchSize: uploadBatchSize,
				flowType: this.flowType,
			});
		};

		fromDeviceEventDisposables.add(expressEventListener(fileEl, "change", processUploadFiles));
	};

	private initUploadFromPhone = async (parentEl: HTMLElement) => {
		const folderId = parentEl.dataset.folderid || "";
		const uploadBatchSize = parseInt(parentEl.dataset.uploadbatchsize || "5", 10);
		let sessionPoller: ISessionPoller;

		const onBeforeOpen = (popupEl: HTMLElement) => {
			const qrContainerEl = expressQuerySelector<HTMLElement>(popupEl, ".technical-qr-container");
			if (!qrContainerEl) return;

			const headEl = expressQuerySelector<HTMLElement>(document, "head", true);
			const qrCodeScript = document.createElement("script");
			qrCodeScript.src = window.context.qrCodePath;
			qrCodeScript.onload = () => {
				new QRCode(qrContainerEl, {
					text:
						window.location.protocol +
						"//" +
						window.location.host +
						popupEl.dataset.uploadpageurl +
						"&accid=" +
						getCookie("accid") +
						"&bs=" +
						uploadBatchSize +
						"&mul=" +
						(this.maxSelectableItems > 1) +
						"&flowtype=" +
						this.flowType,
					width: 200,
					height: 200,
					colorDark: "#000000",
					colorLight: "#ffffff",
					useSVG: false,
					correctLevel: 3,
				});

				// Start polling the upload session

				let uploadIndicator: IUploadDialog | undefined = null;
				const onPollingFinished = (failedPollingFileNames: string[], pollingErrors: number) => {
					const importedFileIds = sessionPoller.getImportedFileIds();
					if (pollingErrors > 0 && this.pageLog) {
						const isError = importedFileIds.length == 0;
						const message = isError
							? this.allFailedLabel.replace(/\{0\}/g, failedPollingFileNames.length.toString())
							: this.someFailedLabel
									.replace(/\{0\}/g, failedPollingFileNames.length.toString())
									.replace(/\{1\}/g, failedPollingFileNames.join(","))
									.replace(/\{2\}/g, importedFileIds.length.toString());

						this.pageLog.updateMessageAndStyle(
							isError ? PageLogStyle.Error : PageLogStyle.WarningOrange,
							message,
							true,
						);
					}

					getPhotosAsync(window.context.pointOfSaleId, getCookie("accid"), folderId, importedFileIds).then(
						async (smartphotoPhotos) => {
							try {
								this.uploadFromPhonePopup.close();
								uploadIndicator && uploadIndicator.updateProgress(smartphotoPhotos.length);
								await this.onImportPhotosFinished(smartphotoPhotos, failedPollingFileNames);
							} catch (errorMessage) {
								await this.openPhotoStoresPopup({
									onBeforeClose: () => {
										return;
									},
									errorMessage,
								});
							}
						},
					);
				};
				if (!sessionPoller) {
					sessionPoller = createSessionPoller({
						onPollingFinished,
						onFileUploaded: (amountUploadedFiles: number, totalAmountOfFiles: number) => {
							if (amountUploadedFiles == 0 && totalAmountOfFiles == 0) return; // upload not started yet

							if (uploadIndicator) {
								uploadIndicator.setTotalUploads(uploadIndicator.id, totalAmountOfFiles);
							} else {
								uploadIndicator = createUploadIndicator(
									totalAmountOfFiles,
									this.uploadIndicator,
									this.refreshPhotoContainer,
									true,
								);
							}

							amountUploadedFiles != totalAmountOfFiles && uploadIndicator.updateProgress(amountUploadedFiles);
						},
					});
					sessionPoller.startSessionPolling(this.uploadFromPhonePopup.popup.dataset.sessionid);
				}
			};
			headEl.appendChild(qrCodeScript);
		};

		this.uploadFromPhonePopup = await createPopup({
			dialogId: "UploadFromPhoneDialog",
			view: "UploadFromPhoneDialog",
			forceRefresh: true,
			data: {
				folderId: parentEl.dataset.folderid || "",
				flow: this.flowType.toString(),
			},
			onBeforeOpen: onBeforeOpen,
		});

		// close the photo sources popup
		this.selectPhotoSourcePopup.close();

		// open the  popup
		await this.uploadFromPhonePopup.openAsync();
	};

	private initPhotoSources = (): void => {
		const photoSourceEls = expressQuerySelectorAll<HTMLElement>(document, ".technical-photosource");
		photoSourceEls.forEach((photoSourceEl) => {
			const storeType = photoSourceEl.dataset.storetype.toLowerCase();
			if (isSafari()) {
				getPhotoStoreAsync(storeType).then((storeData) => {
					this.photostoresData[storeType] = storeData;
				});
			}
			this.photoSourcesEventDisposables.add(
				expressEventListener(photoSourceEl, "click", async (ev: any) => {
					ev.preventDefault();
					ev.stopPropagation();
					this.pageLog && this.pageLog.hideLog();
					const storeType = photoSourceEl.dataset.storetype.toLowerCase();
					switch (storeType) {
						case "facebook":
							this.connectedStoreType = StoreType.Facebook;
							break;
						case "googlephotos":
							this.connectedStoreType = StoreType.GooglePhotos;
							break;
						case "local":
							this.connectedStoreType = StoreType.Local;
							break;
						case "swisscom":
							this.connectedStoreType = StoreType.SwissCom;
							break;
						default:
							this.connectedStoreType = StoreType.eXpress;
							break;
					}

					const connectedToStoreAsync = async (connectedStore: IPhotoStore | null) => {
						if (isSafari()) this.photostoresData[connectedStore.Code.toLowerCase()] = connectedStore;
						if (
							!connectedStore ||
							!connectedStore.IsConnected ||
							(connectedStore.UseExternalPhotoPicker && !connectedStore.HasAlbumAccess)
						)
							return;

						if (this.connectedSourcePopup) await this.closeImportFromAsync();

						const connectedPhotoSource = expressQuerySelector(
							document,
							".technical-photosource[data-storetype='" + connectedStore.Code.toLowerCase() + "']",
						);
						const connectedTooltip = expressQuerySelector<HTMLElement>(
							connectedPhotoSource,
							".technical-photosource-connectedas",
						);
						const toolTipComponent = initTooltip({
							containerEl: connectedTooltip,
							buttonEl: null,
						});
						const connectedTooltipLabel = expressQuerySelector(
							connectedTooltip,
							".technical-photosource-connectedas-label",
						);
						connectedTooltipLabel.innerHTML = connectedTooltipLabel.innerHTML.replace(
							"{0}",
							connectedStore.ConnectedUserName,
						);
						toolTipComponent.closeToolTip();

						if (connectedStore.StoreType === StoreType.GooglePhotos && connectedStore.UseExternalPhotoPicker) {
							this.openImportFromGoogleAsync(connectedStore);
							return;
						}

						this.openImportFromPopup(connectedStore);
					};

					let photoStore: IPhotoStore;
					if (isSafari()) photoStore = this.photostoresData[storeType];
					else photoStore = await showLoadingDialog(getPhotoStoreAsync(this.connectedStoreType.toString()));
					await connectToAsync(photoStore, connectedToStoreAsync, photoStore.UseExternalPhotoPicker);
				}),
			);
		});
	};

	private openImportFromPopup = async (connectedStore: IPhotoStore) => {
		const frameEl = document.createElement("iframe");
		frameEl.className = "m-popup__iframe";
		let url = connectedStore.ImportPhotosUrl;
		url = addParameter("maxSelectableItems", this.maxSelectableItems.toString(), url);
		frameEl.src = url;

		// create content-div
		const corePopupContent = document.createElement("div");
		corePopupContent.classList.add("m-popup__body");
		corePopupContent.style.width = "100%";
		corePopupContent.style.padding = "0";
		corePopupContent.append(frameEl);

		// create container-div
		const corePopup = document.createElement("div");
		corePopup.classList.add("m-popup__content");
		corePopup.classList.add("technical-popup-content");
		corePopup.style.display = "flex";
		corePopup.append(corePopupContent);

		// start listening for messages
		if (!this.broadcastChannel) {
			this.broadcastChannel = new BroadcastChannel("connectedToStorePartner");
			this.broadcastChannel.onmessage = this.messageEvent;
		}

		const options: IPopupOptions = {
			dialogId: "importphotopopup_" + connectedStore.StoreType,
			view: "GenericPopup",
			forceRefresh: true,
			onBeforeClose: () => {
				// close the connection to the channel to prevent duplicate message-triggers
				this.broadcastChannel && this.broadcastChannel.close();
				this.broadcastChannel = undefined;
			},
			data: {
				BodyText: "",
			},
		};

		this.connectedSourcePopup = await createPopup(options);
		this.connectedSourcePopup.replaceContent(corePopup);

		// close the photo sources popup
		this.selectPhotoSourcePopup.close();

		// open the connected source popup
		await this.connectedSourcePopup.openAsync();
	};

	private messageEvent = async (e) => {
		if (e.origin !== window.origin) return;
		if (!e.data || !e.data.command) return;

		switch (e.data.command) {
			case "CloseImportPhotosPopUp":
				await this.closeImportFromAsync();
				break;
			case "ImportPhotosBack": {
				await this.closeImportFromAsync();
				await this.openPhotoStoresPopup();
				break;
			}
			case "ImportPhotosFinished": {
				const photoImport = e.data.detail as IPhotoImport | null;
				await this.importPhotosFinished(photoImport);
				break;
			}
			case "ImportPhotosPopUpError": {
				let photoStoreErrorMessage = "";
				if (e.data.detail.errorCode == "unableToConnectToPartner") {
					photoStoreErrorMessage = await getLabelAsync(
						"Label.AlbumMain." + StoreType[this.connectedStoreType] + ".CouldNotConnect",
					);
				}

				await this.closeImportFromAsync();
				await this.openPhotoStoresPopup({ errorMessage: photoStoreErrorMessage });
				break;
			}
		}
	};

	private importPhotosFinished = async (photoImport: IPhotoImport | null) => {
		if (photoImport) {
			const isPartnerImport = photoImport.storeType !== StoreType.eXpress;
			const totalItems = photoImport.selection?.length ?? 0;

			const uploadDialog: IUploadDialog | undefined =
				isPartnerImport && totalItems > 0
					? this.isBlockingUpload
						? await createUploadDialog(totalItems)
						: createUploadIndicator(totalItems, this.uploadIndicator, this.refreshPhotoContainer)
					: undefined;

			// non-blocking? Close the photo-sources popup (and the connected partner popup) right away so the user isn't blocked
			if (uploadDialog && !this.isBlockingUpload) {
				this.selectPhotoSourcePopup.close();
				await this.closeImportFromAsync();
			}

			const convertPhotos = async (photoImport: IPhotoImport): Promise<void> => {
				const smartphotoPhotos = await this.convertToSmartphotoPhotosAsync(photoImport, uploadDialog);

				try {
					this.selectPhotoSourcePopup.close(); //Close the photo-sources-popup when the user has made his selection
					await this.onImportPhotosFinished(smartphotoPhotos, []);
				} catch (errorMessage) {
					await this.openPhotoStoresPopup({ errorMessage });
				}
			};

			if (uploadDialog) await convertPhotos(photoImport);
			else await showLoadingDialog(convertPhotos(photoImport));
		}

		await this.closeImportFromAsync();
	};

	public closeImportFromAsync = async () => {
		// close connection
		await this.closeConnectionAsync();

		this.connectedSourcePopup && this.connectedSourcePopup.close();
	};

	private showPopupBlockedBanner = (store: IPhotoStore) => {
		const bannerEl = document.querySelector<HTMLElement>(".technical-mediapartner-connected-popupblocked");
		if (!bannerEl) return;

		bannerEl.innerHTML = bannerEl.innerHTML.replace("{0}", store.Name);
		bannerEl.classList.remove("u-hide");
	};

	private openImportFromGoogleAsync = async (connectedSource: IPhotoStore) => {
		// reuse existing picker
		if (!this.externalPicker) this.externalPicker = new ExternalPhotoPicker();

		await showLoadingDialog(this.externalPicker.createExternalPhotoPicker(this.maxSelectableItems));

		// a window is already open? Try to close it
		if (this.pickerWindow) this.pickerWindow.close();

		// open the picker
		this.pickerWindow = this.externalPicker.openPicker();
		if (this.pickerWindow == null) {
			// We couldn't open the popup => show the banner
			// The customer could open the popup via the browser, so we can't stop here
			// and have to start polling the session
			this.showPopupBlockedBanner(connectedSource);
		}

		// wait for feedback
		const userHasSelectedPhotos = await this.externalPicker.startPickerPolling();
		if (!userHasSelectedPhotos) return;

		const selectPhotosAndCloseConnection = async (externalPicker: ExternalPhotoPicker): Promise<PartnerPhoto[]> => {
			const photos = await externalPicker.getSelectedPhotos();
			await this.closeConnectionAsync();
			return photos;
		};

		const photos = await showLoadingDialog(selectPhotosAndCloseConnection(this.externalPicker));

		const totalItems = photos?.length ?? 0;
		const uploadDialog: IUploadDialog | undefined =
			totalItems > 0
				? this.isBlockingUpload
					? await createUploadDialog(totalItems)
					: createUploadIndicator(totalItems, this.uploadIndicator, this.refreshPhotoContainer)
				: undefined;

		// non-blocking? Close the photo-sources popup right away so the user isn't blocked
		if (uploadDialog && !this.isBlockingUpload) this.selectPhotoSourcePopup.close();

		const convertPhotos = async (): Promise<void> => {
			const data = { selection: photos, storeType: StoreType.GooglePhotos } as IPhotoImport | null;
			const smartphotoPhotos = await this.convertToSmartphotoPhotosAsync(data, uploadDialog);

			// no photos selected? Show a message and return
			if (!smartphotoPhotos) {
				if (this.pageLog && this.imageInvalidLabel) {
					this.pageLog.updateMessageAndStyle(PageLogStyle.Error, this.imageInvalidLabel);
				}
				return null;
			}

			this.selectPhotoSourcePopup.close(); //Close the photo-sources-popup when the user has made his selection
			try {
				await this.onImportPhotosFinished(smartphotoPhotos, []);
			} catch (errorMessage) {
				await this.openPhotoStoresPopup({ errorMessage });
			}
		};

		if (uploadDialog) await convertPhotos();
		else await showLoadingDialog(convertPhotos());
	};

	private convertToSmartphotoPhotosAsync = async (
		data: IPhotoImport,
		uploadDialog?: IUploadDialog,
	): Promise<IPhoto[]> => {
		const photos = data.selection;
		if (!photos || photos.length == 0) return Promise.resolve(undefined);

		// photos already eXpress? => no need to convert, just return them
		if (data.storeType == StoreType.eXpress) {
			if (this.folderId)
				return await copyPhotosToFolder(
					getCookie("accid"),
					window.context.pointOfSaleId,
					data.selection.map((photo) => photo.fileId),
					this.folderId,
				);
			else return Promise.resolve(photos);
		}

		const token = await getToken();
		const productCategoryGroupCode = (getParameter("pcg", undefined, false) || "").toLowerCase();
		uploadDialog && uploadDialog.updateProgress && uploadDialog.updateProgress(0);
		const downloader = new Downloader(this.flowType, token, productCategoryGroupCode, uploadDialog);
		return await downloader.addToQueueAsync(photos as PartnerPhoto[], this.folderId);
	};

	private closeConnectionAsync = async () => {
		if (this.externalPicker) {
			await this.externalPicker.deleteExternalPhotoPicker();
			this.externalPicker = undefined;
		}

		this.pickerWindow && this.pickerWindow.close();
		this.pickerWindow = null;
	};
}
